import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import ImageUploadField from "./ImageUploadField";
import ModelUploadField from "./ModelUploadField";

const MOUNT_OPTIONS = ["wall", "table", "floor", "ceiling"];

const emptyForm = {
  id: "",
  category_id: "",
  brand_id: "",
  type: "display",
  name: "",
  width: 0.2,
  height: 0.2,
  depth: 0.2,
  mount_on: "wall",
  color: "#1a1a1a",
  accent_color: "#3b82f6",
  coverage_angle: 360,
  coverage_distance: 3,
  image_url: "",
  model_url: "",
  model_scale: 1,
  model_rotation_y: 0,
  meta: "{}",
  is_active: true,
};

export default function ProductForm() {
  const { id } = useParams(); // undefined kalau mode "new"
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [metaError, setMetaError] = useState("");

  useEffect(() => {
    const loadRefs = async () => {
      const [{ data: cats }, { data: brs }, { data: tps }] = await Promise.all([
        supabase.from("categories").select("*").order("sort_order"),
        supabase.from("brands").select("*").order("sort_order"),
        supabase.from("types").select("*").order("sort_order"),
      ]);
      setCategories(cats || []);
      setBrands(brs || []);
      setTypes(tps || []);
    };
    loadRefs();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    const loadProduct = async () => {
      setLoading(true);
      const { data, error: fetchErr } = await supabase.from("products").select("*").eq("id", id).single();
      if (fetchErr) {
        setError(fetchErr.message);
      } else if (data) {
        setForm({
          ...data,
          meta: JSON.stringify(data.meta || {}, null, 2),
        });
      }
      setLoading(false);
    };
    loadProduct();
  }, [id, isEdit]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMetaError("");

    let parsedMeta;
    try {
      parsedMeta = form.meta.trim() ? JSON.parse(form.meta) : {};
    } catch {
      setMetaError("Format JSON tidak valid pada field Meta/Spesifikasi.");
      return;
    }

    if (!form.id.trim()) {
      setError("ID produk wajib diisi (contoh: samsung-wall-146).");
      return;
    }

    const payload = {
      id: form.id.trim(),
      category_id: form.category_id,
      brand_id: form.brand_id,
      type: form.type,
      name: form.name,
      width: Number(form.width),
      height: Number(form.height),
      depth: Number(form.depth),
      mount_on: form.mount_on,
      color: form.color,
      accent_color: form.accent_color,
      coverage_angle: Number(form.coverage_angle),
      coverage_distance: Number(form.coverage_distance),
      image_url: form.image_url || null,
      model_url: form.model_url || null,
      model_scale: Number(form.model_scale) || 1,
      model_rotation_y: Number(form.model_rotation_y) || 0,
      meta: parsedMeta,
      is_active: form.is_active,
    };

    setSaving(true);
    const query = isEdit
      ? supabase.from("products").update(payload).eq("id", id)
      : supabase.from("products").insert(payload);

    const { error: saveErr } = await query;
    setSaving(false);

    if (saveErr) {
      setError(saveErr.message);
      return;
    }
    navigate("/admin");
  };

  if (loading) {
    return <div style={{ padding: 28, color: "#94a3b8", fontSize: 13 }}>Memuat produk...</div>;
  }

  return (
    <div style={{ padding: "24px 28px", maxWidth: 720 }}>
      <Link to="/admin" style={{ fontSize: 12, color: "#64748b", textDecoration: "none" }}>← Kembali ke daftar produk</Link>
      <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", marginTop: 10, marginBottom: 20 }}>
        {isEdit ? `Edit: ${form.name}` : "Tambah Produk Baru"}
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: 13, padding: "10px 14px", borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24 }}>
        <Row label="ID Produk" hint="unik, slug kebab-case, tidak bisa diubah setelah tersimpan">
          <input
            value={form.id}
            onChange={(e) => update("id", e.target.value)}
            disabled={isEdit}
            placeholder="contoh: samsung-wall-146"
            style={inputStyle}
          />
        </Row>

        <Row label="Nama Produk">
          <input value={form.name} onChange={(e) => update("name", e.target.value)} required style={inputStyle} />
        </Row>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Row label="Kategori">
            <select value={form.category_id} onChange={(e) => update("category_id", e.target.value)} required style={inputStyle}>
              <option value="">Pilih kategori</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </Row>
          <Row label="Brand">
            <select value={form.brand_id} onChange={(e) => update("brand_id", e.target.value)} required style={inputStyle}>
              <option value="">Pilih brand</option>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
            </select>
          </Row>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Row label="Tipe (untuk ikon & warna coverage)">
            <select value={form.type} onChange={(e) => update("type", e.target.value)} style={inputStyle}>
              <option value="">Pilih tipe</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Row>
          <Row label="Mount On">
            <select value={form.mount_on} onChange={(e) => update("mount_on", e.target.value)} style={inputStyle}>
              {MOUNT_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Row>
        </div>

        <Row label="Dimensi dalam meter (Width × Height × Depth)">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <input type="number" step="0.001" value={form.width} onChange={(e) => update("width", e.target.value)} style={inputStyle} placeholder="Width" />
            <input type="number" step="0.001" value={form.height} onChange={(e) => update("height", e.target.value)} style={inputStyle} placeholder="Height" />
            <input type="number" step="0.001" value={form.depth} onChange={(e) => update("depth", e.target.value)} style={inputStyle} placeholder="Depth" />
          </div>
        </Row>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Row label="Warna Dasar (fallback 3D)">
            <input type="color" value={form.color} onChange={(e) => update("color", e.target.value)} style={{ ...inputStyle, padding: 4, height: 38 }} />
          </Row>
          <Row label="Warna Aksen">
            <input type="color" value={form.accent_color} onChange={(e) => update("accent_color", e.target.value)} style={{ ...inputStyle, padding: 4, height: 38 }} />
          </Row>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Row label="Coverage Angle (°)" hint="≥300 = omnidirectional">
            <input type="number" value={form.coverage_angle} onChange={(e) => update("coverage_angle", e.target.value)} style={inputStyle} />
          </Row>
          <Row label="Coverage Distance (m)">
            <input type="number" step="0.1" value={form.coverage_distance} onChange={(e) => update("coverage_distance", e.target.value)} style={inputStyle} />
          </Row>
        </div>

        <Row label="Gambar Produk (thumbnail untuk sidebar)">
          <ImageUploadField
            value={form.image_url}
            onChange={(url) => update("image_url", url)}
            productId={form.id}
          />
        </Row>

        <Row label="Model 3D (.glb)" hint="kosongkan untuk memakai geometri 3D bawaan sistem">
          <ModelUploadField
            value={form.model_url}
            onChange={(url) => update("model_url", url)}
            productId={form.id}
            rotationY={Number(form.model_rotation_y) || 0}
          />
        </Row>

        <Row label="Skala Model (faktor koreksi manual)" hint="biarkan 1 kalau auto-scaling sudah pas. Ubah kalau model terlihat terlalu besar/kecil setelah dicek di RoomDesigner">
          <input type="number" step="0.01" min="0.01" value={form.model_scale} onChange={(e) => update("model_scale", e.target.value)} style={inputStyle} />
        </Row>

        <Row
          label="Rotasi Koreksi Arah Model"
          hint='ubah kalau bagian "depan" model (lensa kamera, grille speaker, dll) menghadap arah yang salah saat ditempatkan — preview di atas ikut berputar real-time'
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={form.model_rotation_y}
              onChange={(e) => update("model_rotation_y", e.target.value)}
              style={{ flex: 1, accentColor: "#3b82f6" }}
            />
            <div style={{
              minWidth: 52, textAlign: "center", padding: "4px 8px", borderRadius: 6,
              background: "#eff6ff", border: "1px solid #bfdbfe", fontSize: 12,
              fontWeight: 700, color: "#3b82f6", fontFamily: "monospace",
            }}>
              {form.model_rotation_y > 0 ? `+${form.model_rotation_y}` : form.model_rotation_y}°
            </div>
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
            {[-180, -90, 0, 90, 180].map((deg) => (
              <button
                key={deg}
                type="button"
                onClick={() => update("model_rotation_y", deg)}
                style={{
                  flex: 1, padding: "4px 2px", borderRadius: 5, fontSize: 11, fontWeight: 600,
                  border: `1px solid ${Number(form.model_rotation_y) === deg ? "#3b82f6" : "#cbd5e1"}`,
                  background: Number(form.model_rotation_y) === deg ? "#3b82f6" : "#fff",
                  color: Number(form.model_rotation_y) === deg ? "#fff" : "#3b82f6",
                  cursor: "pointer",
                }}
              >
                {deg}°
              </button>
            ))}
          </div>
        </Row>

        <Row label="Meta / Spesifikasi Teknis (format JSON)" hint='contoh: {"driver": "6.5in woofer", "power": "100W"}'>
          <textarea
            value={form.meta}
            onChange={(e) => update("meta", e.target.value)}
            rows={6}
            style={{ ...inputStyle, fontFamily: "monospace", fontSize: 12, resize: "vertical" }}
          />
          {metaError && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>{metaError}</div>}
        </Row>

        <Row label="Status">
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155" }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => update("is_active", e.target.checked)} />
            Tampilkan di katalog (aktif)
          </label>
        </Row>

        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button
            type="submit"
            disabled={saving}
            style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#3b82f6", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            {saving ? "Menyimpan..." : isEdit ? "Simpan Perubahan" : "Tambah Produk"}
          </button>
          <Link to="/admin" style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #e2e8f0", color: "#475569", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
            Batal
          </Link>
        </div>
      </form>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>
        {label}
      </label>
      {children}
      {hint && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1",
  fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit",
};